/**
 * Conveyancer-portal app shell.
 *
 * Routes are split with React.lazy() so each page is its own JS chunk
 * and the initial bundle only ships AuthPage. Each lazy route is
 * wrapped in a RouteErrorBoundary from @bc/ui so a render error in
 * one page can't take down the entire app — the user lands on the
 * shared error fallback and can still navigate.
 *
 * The route guards (`ProtectedRoute`, `PublicRoute`, `ServiceOfferingRoute`)
 * are unchanged from pre-M5 and live inline because they're tightly
 * coupled to the route shape.
 */

import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { FOOTER_LINKS } from "./lib/footerLinks";

import { InstallPrompt, RootErrorBoundary, RouteErrorBoundary, DialogProvider, CookieConsent, Footer, PrivacyPolicy, ConveyancerTermsOfService, ConveyancingFees } from "@bc/ui";

import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

const AuthPage = lazy(() => import("./pages/AuthPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const SessionDetail = lazy(() => import("./pages/SessionDetail"));
const ServiceOfferingPage = lazy(() => import("./pages/ServiceOfferingPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const AdminVerificationQueue = lazy(() => import("./pages/AdminVerificationQueue"));
const AdminConveyancers = lazy(() => import("./pages/AdminConveyancers"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CodeOfConduct = lazy(() => import("./pages/CodeOfConduct"));
const PopiaNotice = lazy(() => import("./pages/PopiaNotice"));


function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-gray-600 animate-pulse">Loading...</div>
    </div>
  );
}

// Mobile browsers restore old scroll positions on back/forward; take manual
// control once so every navigation starts at the top of the page.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/** Reset scroll to the top on every route change (body-scrolling pages). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * Footer for body-scrolling pages. The dashboard pins its own copy inside
 * its fixed viewport frame (Dashboard.js), so rendering it here too would
 * make the document taller than 100dvh and let the top bar scroll away.
 */
function SiteFooter() {
  const { pathname } = useLocation();
  if (pathname === "/dashboard") return null;
  return <Footer links={FOOTER_LINKS} />;
}

function withRouteBoundary(element) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
    </RouteErrorBoundary>
  );
}


// -----------------------------------------------------------------------------
// Route guards
// -----------------------------------------------------------------------------

function ProtectedRoute({ children, requireServiceOffering = false }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (requireServiceOffering && !user.service_offering_completed) {
    return <Navigate to="/service-offering" replace />;
  }
  return children;
}

function PublicRoute({ children, allowLoggedIn = false }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;

  if (user && !allowLoggedIn) {
    // Honour the "amend" intent stored at logout-time.
    const wantsToAmend = localStorage.getItem("amendServiceOffering");
    if (wantsToAmend === "true") {
      localStorage.removeItem("amendServiceOffering");
      return <Navigate to="/service-offering" replace />;
    }
    if (!user.service_offering_completed) {
      return <Navigate to="/service-offering" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function ServiceOfferingRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  // New-registration mode: no user yet, but the signup form handed us an
  // in-memory account via route state. Let the wizard through — it creates the
  // account at its final submit.
  if (!user && !location.state?.account) return <Navigate to="/" replace />;
  return children;
}


function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/"
        element={withRouteBoundary(
          <PublicRoute>
            <AuthPage />
          </PublicRoute>,
        )}
      />
      <Route
        path="/dashboard"
        element={withRouteBoundary(
          <ProtectedRoute requireServiceOffering>
            <Dashboard />
          </ProtectedRoute>,
        )}
      />
      <Route
        path="/session/:sessionId"
        element={withRouteBoundary(
          <ProtectedRoute requireServiceOffering>
            <SessionDetail />
          </ProtectedRoute>,
        )}
      />
      <Route
        path="/service-offering"
        element={withRouteBoundary(
          <ServiceOfferingRoute>
            <ServiceOfferingPage />
          </ServiceOfferingRoute>,
        )}
      />
      <Route
        path="/account"
        element={withRouteBoundary(
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>,
        )}
      />
      <Route
        path="/admin/verification"
        element={withRouteBoundary(
          <AdminRoute>
            <AdminVerificationQueue />
          </AdminRoute>,
        )}
      />
      <Route
        path="/admin/conveyancers"
        element={withRouteBoundary(
          <AdminRoute>
            <AdminConveyancers />
          </AdminRoute>,
        )}
      />
      <Route path="/forgot-password" element={withRouteBoundary(<ForgotPassword />)} />
      <Route path="/reset-password" element={withRouteBoundary(<ResetPassword />)} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<ConveyancerTermsOfService />} />
      <Route path="/code-of-conduct" element={withRouteBoundary(<CodeOfConduct />)} />
      <Route path="/popia-notice" element={withRouteBoundary(<PopiaNotice />)} />
      <Route path="/conveyancing-fees" element={<ConveyancingFees />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}


function App() {
  return (
    <RootErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
          <DialogProvider>
            <ScrollToTop />
            <AppRoutes />
            <SiteFooter />
            <CookieConsent />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: "#ffffff",
                  border: "1px solid #dfdbd1",
                  color: "#0E1726",
                },
              }}
            />
            {/* M6: PWA install hint. Suppresses on iOS, only shows after
                the second visit, remembers dismissal. Inherits BidConvey
                navy via the className override. */}
            <InstallPrompt
              appName="BidConvey Conveyancer Portal"
              className="bg-[#1A2040] text-white"
            />
          </DialogProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </RootErrorBoundary>
  );
}

export default App;
